import * as THREE from 'three';

/**
 * UniversalPlacementSystem - Enhanced with matrix-based coordinate transformations
 * Handles object placement on walls and floors with proper coordinate transformations
 */
export class UniversalPlacementSystem {
  constructor(surfaceData) {
    this.surfaces = new Map();
    this.occupiedCells = new Map(); // Track occupied grid cells
    this.wallPattern = /^wall_\d+/i;
    this.floorPattern = /^floor_\w+/i;
    
    // Reconstruct Three.js objects from backend data
    if (surfaceData) {
      surfaceData.forEach(surface => {
        this.surfaces.set(surface.id, this.deserializeSurface(surface));
      });
    }
  }

  deserializeSurface(data) {
    return {
      ...data,
      center: new THREE.Vector3(...data.center),
      normal: new THREE.Vector3(...data.normal),
      origin: new THREE.Vector3(...data.origin),
      xAxis: new THREE.Vector3(...data.xAxis),
      yAxis: new THREE.Vector3(...data.yAxis),
      zAxis: new THREE.Vector3(...data.zAxis),
      localToWorld: new THREE.Matrix4().fromArray(data.localToWorld),
      worldToLocal: new THREE.Matrix4().fromArray(data.worldToLocal),
      quaternion: new THREE.Quaternion(...data.quaternion)
    };
  }

  placeObject(objectMesh, surfaceId, gridX, gridY, options = {}) {
    const surface = this.surfaces.get(surfaceId);
    if (!surface) {
      console.error(`Surface ${surfaceId} not found`);
      return false;
    }

    const {
      checkCollisions = true,
      objectType = 'painting' // Default object type
    } = options;

    console.log(`\n=== UNIVERSAL PLACEMENT SYSTEM ===`);
    console.log(`Placing object on surface: ${surfaceId} (${surface.type})`);
    console.log(`Grid position: (${gridX}, ${gridY})`);
    console.log(`Object type: ${objectType}`);

    // Validate placement
    if (!this.validatePlacement(gridX, gridY, surface, objectMesh)) {
      console.error('Placement validation failed');
      return false;
    }

    // Check if cell is occupied
    if (checkCollisions && this.isCellOccupied(surfaceId, gridX, gridY)) {
      console.warn(`Cell ${gridX},${gridY} on ${surfaceId} is occupied`);
      return false;
    }

    // Use matrix-based coordinate transformation
    const transformResult = this.gridToWorldMatrix(gridX, gridY, surface, objectType);
    if (!transformResult) {
      console.error('Failed to transform grid coordinates to world position');
      return false;
    }

    const { position: worldPos, rotation } = transformResult;

    // Apply position
    objectMesh.position.set(worldPos.x, worldPos.y, worldPos.z);
    console.log(`World position: (${worldPos.x.toFixed(3)}, ${worldPos.y.toFixed(3)}, ${worldPos.z.toFixed(3)})`);

    // Apply rotation
    if (surface.type === 'wall') {
      // Use calculated rotation for proper wall orientation
      objectMesh.rotation.y = rotation;
      console.log(`Applied wall rotation: ${(rotation * 180 / Math.PI).toFixed(1)}°`);
    } else {
      // Floor objects can have custom rotation
      if (options.rotation !== undefined) {
        objectMesh.rotation.y = options.rotation;
      } else {
        objectMesh.rotation.y = rotation;
      }
    }

    // Mark cell as occupied
    this.markCellOccupied(surfaceId, gridX, gridY, objectMesh.uuid);

    // Store placement data on the object
    objectMesh.userData.placement = {
      surfaceId,
      gridX,
      gridY,
      worldPosition: worldPos,
      rotation: rotation,
      objectType: objectType
    };

    console.log('Object placement successful');
    return true;
  }

  // Grid management
  isCellOccupied(surfaceId, x, y) {
    const key = `${surfaceId}_${x}_${y}`;
    return this.occupiedCells.has(key);
  }

  markCellOccupied(surfaceId, x, y, objectId) {
    const key = `${surfaceId}_${x}_${y}`;
    this.occupiedCells.set(key, objectId);
  }

  freeCell(surfaceId, x, y) {
    const key = `${surfaceId}_${x}_${y}`;
    this.occupiedCells.delete(key);
  }

  // Raycasting helper for drag and drop
  getSurfaceFromRaycast(raycaster, scene) {
    // Get all surface meshes
    const surfaceMeshes = [];
    
    scene.traverse((child) => {
      if (child.isMesh && (
        this.wallPattern.test(child.name) || 
        this.floorPattern.test(child.name)
      )) {
        surfaceMeshes.push(child);
      }
    });

    const intersects = raycaster.intersectObjects(surfaceMeshes);
    
    if (intersects.length > 0) {
      const hit = intersects[0];
      const surface = this.surfaces.get(hit.object.name);
      
      if (surface) {
        // Convert hit point to grid coordinates
        const localPoint = hit.point.clone().applyMatrix4(surface.worldToLocal);
        
        let gridX, gridY;
        if (surface.type === 'floor') {
          gridX = Math.floor((localPoint.x / surface.width + 0.5) * 10);
          gridY = Math.floor((localPoint.z / surface.height + 0.5) * 10);
        } else {
          // For walls, flip Y coordinate back to match UI coordinate system
          const normalizedY = localPoint.y / surface.height;
          gridX = Math.floor((localPoint.x / surface.width + 0.5) * 10);
          gridY = Math.floor((0.5 - normalizedY) * 10); // Flip Y back for walls
        }

        return {
          surface,
          gridX: Math.max(0, Math.min(9, gridX)),
          gridY: Math.max(0, Math.min(9, gridY)),
          worldPoint: hit.point,
          localPoint
        };
      }
    }

    return null;
  }

  // Visual helpers
  createGridHelper(surfaceId, gridSize = 10) {
    const surface = this.surfaces.get(surfaceId);
    if (!surface) return null;

    const geometry = new THREE.BufferGeometry();
    const material = new THREE.LineBasicMaterial({ 
      color: 0x00ff00, 
      opacity: 0.3, 
      transparent: true 
    });

    const points = [];

    // Create grid lines
    for (let i = 0; i <= gridSize; i++) {
      const t = i / gridSize - 0.5;

      if (surface.type === 'floor') {
        // Horizontal lines
        points.push(
          new THREE.Vector3(-surface.width/2, 0, t * surface.height),
          new THREE.Vector3(surface.width/2, 0, t * surface.height)
        );
        // Vertical lines
        points.push(
          new THREE.Vector3(t * surface.width, 0, -surface.height/2),
          new THREE.Vector3(t * surface.width, 0, surface.height/2)
        );
      } else {
        // Horizontal lines
        points.push(
          new THREE.Vector3(-surface.width/2, t * surface.height, 0),
          new THREE.Vector3(surface.width/2, t * surface.height, 0)
        );
        // Vertical lines
        points.push(
          new THREE.Vector3(t * surface.width, -surface.height/2, 0),
          new THREE.Vector3(t * surface.width, surface.height/2, 0)
        );
      }
    }

    geometry.setFromPoints(points);
    const grid = new THREE.LineSegments(geometry, material);
    
    // Apply surface transformation
    grid.applyMatrix4(surface.localToWorld);
    
    return grid;
  }

  // Convert world coordinates to surface local coordinates
  worldToSurfaceLocal(worldPos, surfaceId) {
    const surface = this.surfaces.get(surfaceId);
    if (!surface) return null;

    return worldPos.clone().applyMatrix4(surface.worldToLocal);
  }

  // Convert surface local coordinates to world coordinates
  surfaceLocalToWorld(localPos, surfaceId) {
    const surface = this.surfaces.get(surfaceId);
    if (!surface) return null;

    return localPos.clone().applyMatrix4(surface.localToWorld);
  }

  // Get grid coordinates from world position
  worldToGridCoordinates(worldPos, surfaceId) {
    const localPos = this.worldToSurfaceLocal(worldPos, surfaceId);
    if (!localPos) return null;

    const surface = this.surfaces.get(surfaceId);
    
    if (surface.type === 'floor') {
      return {
        gridX: Math.floor((localPos.x / surface.width + 0.5) * 10),
        gridY: Math.floor((localPos.z / surface.height + 0.5) * 10)
      };
    } else {
      // For walls, flip Y coordinate back to match UI coordinate system
      const normalizedY = localPos.y / surface.height;
      const gridY = Math.floor((0.5 - normalizedY) * 10); // Flip Y back for walls
      
      return {
        gridX: Math.floor((localPos.x / surface.width + 0.5) * 10),
        gridY: Math.max(0, Math.min(9, gridY))
      };
    }
  }

  // Get world position from grid coordinates
  gridToWorldCoordinates(gridX, gridY, surfaceId) {
    const surface = this.surfaces.get(surfaceId);
    if (!surface) return null;

    let localPos;
    if (surface.type === 'floor') {
      localPos = new THREE.Vector3(
        (gridX / 10 - 0.5) * surface.width,
        0,
        (gridY / 10 - 0.5) * surface.height
      );
    } else {
      // For walls, flip Y coordinate to match 3D coordinate system
      const normalizedY = 0.5 - (gridY / 10); // Flip Y for walls
      localPos = new THREE.Vector3(
        (gridX / 10 - 0.5) * surface.width,
        normalizedY * surface.height,
        0
      );
    }

    return this.surfaceLocalToWorld(localPos, surfaceId);
  }

  // Get all surfaces of a specific type
  getSurfacesByType(type) {
    return Array.from(this.surfaces.values()).filter(surface => surface.type === type);
  }

  // Check if a surface exists
  hasSurface(surfaceId) {
    return this.surfaces.has(surfaceId);
  }

  // Get surface information
  getSurface(surfaceId) {
    return this.surfaces.get(surfaceId);
  }

  // Remove object from grid tracking
  removeObject(objectMesh) {
    const placement = objectMesh.userData.placement;
    if (placement) {
      this.freeCell(placement.surfaceId, placement.gridX, placement.gridY);
      delete objectMesh.userData.placement;
    }
  }

  // Move object to new position
  moveObject(objectMesh, newSurfaceId, newGridX, newGridY, options = {}) {
    // Remove from old position
    this.removeObject(objectMesh);
    
    // Place at new position
    return this.placeObject(objectMesh, newSurfaceId, newGridX, newGridY, options);
  }

  // Get all occupied cells for a surface
  getOccupiedCells(surfaceId) {
    const occupied = [];
    for (const [key, objectId] of this.occupiedCells) {
      if (key.startsWith(`${surfaceId}_`)) {
        const [, x, y] = key.split('_');
        occupied.push({ x: parseInt(x), y: parseInt(y), objectId });
      }
    }
    return occupied;
  }

  // Clear all placement data
  clear() {
    this.occupiedCells.clear();
  }

  // Matrix-based coordinate transformation methods
  
  /**
   * Transform grid coordinates to world coordinates using matrix transformations
   * @param {number} gridX - Grid X coordinate (0-10)
   * @param {number} gridY - Grid Y coordinate (0-10)
   * @param {Object} surface - Surface data with transformation matrices
   * @param {string} objectType - Type of object being placed
   * @returns {Object} - {position: {x, y, z}, rotation: number}
   */
  gridToWorldMatrix(gridX, gridY, surface, objectType = 'painting') {
    console.log(`\n=== MATRIX TRANSFORMATION (Frontend) ===`);
    console.log(`Surface: ${surface.id} (${surface.type})`);
    console.log(`Grid: (${gridX}, ${gridY})`);
    console.log(`Object type: ${objectType}`);

    // Validate surface data
    if (!surface.localToWorld || !surface.localToWorld.elements) {
      console.error('Invalid or missing localToWorld transformation matrix');
      return this.fallbackGridToWorld(gridX, gridY, surface, objectType);
    }

    // Convert grid coordinates to surface-local coordinates
    const localPos = this.gridToLocal(gridX, gridY, surface, objectType);
    console.log(`Local position: (${localPos.x.toFixed(3)}, ${localPos.y.toFixed(3)}, ${localPos.z.toFixed(3)})`);

    // Transform from surface-local to world coordinates using Three.js matrix
    const worldPos = localPos.clone().applyMatrix4(surface.localToWorld);
    console.log(`World position: (${worldPos.x.toFixed(3)}, ${worldPos.y.toFixed(3)}, ${worldPos.z.toFixed(3)})`);

    // Calculate rotation based on surface orientation
    const rotation = this.calculateSurfaceRotation(surface, objectType);
    console.log(`Rotation: ${(rotation * 180 / Math.PI).toFixed(1)}°`);

    return {
      position: { x: worldPos.x, y: worldPos.y, z: worldPos.z },
      rotation: rotation
    };
  }

  /**
   * Convert grid coordinates to surface-local coordinates
   */
  gridToLocal(gridX, gridY, surface, objectType = 'painting') {
    // Get surface dimensions
    const width = surface.width || surface.dimensions?.width || 10;
    const height = surface.height || surface.dimensions?.height || 3;

    // Convert grid coordinates (0-10) to normalized coordinates (-0.5 to +0.5)
    const normalizedX = (gridX / 10) - 0.5;
    // For walls, flip Y coordinate to match 3D coordinate system (bottom = 0, top = positive)
    const normalizedY = surface.type === 'wall' ? 
      0.5 - (gridY / 10) :  // Flip Y for walls (grid 0 = top of wall = +0.5 normalized)
      (gridY / 10) - 0.5;   // Normal Y for floors

    // Scale to surface dimensions
    const localX = normalizedX * width;
    const localY = normalizedY * height;

    // Calculate proper offset
    const offset = this.calculateSurfaceOffset(surface, objectType);

    let localPos;
    if (surface.type === 'wall') {
      // For walls: X = along wall, Y = up wall, Z = away from wall
      localPos = new THREE.Vector3(localX, localY, offset);
    } else if (surface.type === 'floor') {
      // For floors: X = along floor, Y = up from floor, Z = across floor
      localPos = new THREE.Vector3(localX, 0, localY);
    } else {
      // Generic surface
      localPos = new THREE.Vector3(localX, localY, offset);
    }

    return localPos;
  }

  /**
   * Calculate proper object offset from surface
   */
  calculateSurfaceOffset(surface, objectType = 'painting') {
    if (surface.type === 'wall') {
      switch (objectType) {
        case 'painting':
          return 0.05; // 5cm from wall
        case '3d':
          return 0.15; // 15cm from wall
        default:
          return 0.05;
      }
    } else if (surface.type === 'floor') {
      return 0.0; // Objects sit on floor surface
    }
    return 0.05; // Default offset
  }

  /**
   * Calculate correct rotation for objects based on surface orientation
   */
  calculateSurfaceRotation(surface, objectType = 'painting') {
    if (surface.type === 'wall' && objectType === 'painting') {
      // Paintings should face outward from the wall
      const normal = surface.normal;
      
      if (normal) {
        // Use the exact normal from the model without any manipulation
        // Layer 1: Pure 3D geometry - respect model normals exactly
        const rotation = Math.atan2(normal.x, normal.z);
        
        console.log(`Calculated rotation for ${objectType} on ${surface.type}:`);
        console.log(`  Model Normal: (${normal.x.toFixed(3)}, ${normal.y.toFixed(3)}, ${normal.z.toFixed(3)})`);
        console.log(`  Rotation: ${(rotation * 180 / Math.PI).toFixed(1)}°`);
        
        return rotation;
      }
    }

    // For floor objects or other cases, use default rotation
    return 0;
  }

  // Removed ensureOutwardNormal method - using raw model normals only

  /**
   * Fallback transformation for cases where matrix data is invalid
   */
  fallbackGridToWorld(gridX, gridY, surface, objectType = 'painting') {
    console.warn('Using fallback coordinate transformation for surface:', surface.id);

    // Use surface center as base
    const basePos = surface.center || new THREE.Vector3(0, 0, 0);
    const width = surface.width || surface.dimensions?.width || 10;
    const height = surface.height || surface.dimensions?.height || 3;
    const offset = this.calculateSurfaceOffset(surface, objectType);

    // Simple grid-to-world mapping
    const localX = (gridX - 5) * (width / 10);
    const localY = (gridY - 5) * (height / 10);

    let worldPos;
    if (surface.type === 'wall') {
      worldPos = new THREE.Vector3(
        basePos.x + localX,
        basePos.y + localY,
        basePos.z + offset
      );
    } else {
      worldPos = new THREE.Vector3(
        basePos.x + localX,
        basePos.y,
        basePos.z + localY
      );
    }

    const rotation = this.calculateSurfaceRotation(surface, objectType);

    return { 
      position: { x: worldPos.x, y: worldPos.y, z: worldPos.z }, 
      rotation 
    };
  }

  /**
   * Validate that object placement is within surface bounds
   * @param {number} gridX - Grid X coordinate
   * @param {number} gridY - Grid Y coordinate
   * @param {Object} surface - Surface data
   * @param {Object} objectMesh - Object mesh for size calculations
   * @returns {boolean} - True if placement is valid
   */
  validatePlacement(gridX, gridY, surface, objectMesh = null) {
    // Check grid bounds
    if (gridX < 0 || gridX > 10 || gridY < 0 || gridY > 10) {
      console.warn(`Grid coordinates out of bounds: (${gridX}, ${gridY})`);
      return false;
    }

    // Check if object fits within surface dimensions
    if (objectMesh) {
      const bbox = new THREE.Box3().setFromObject(objectMesh);
      const objectSize = bbox.getSize(new THREE.Vector3());
      
      const surfaceWidth = surface.width || surface.dimensions?.width || 10;
      const surfaceHeight = surface.height || surface.dimensions?.height || 3;
      
      // Check if object is too large for the surface
      if (surface.type === 'wall') {
        if (objectSize.x > surfaceWidth * 0.8 || objectSize.y > surfaceHeight * 0.8) {
          console.warn(`Object too large for wall surface: ${objectSize.x.toFixed(2)}x${objectSize.y.toFixed(2)} vs ${surfaceWidth.toFixed(2)}x${surfaceHeight.toFixed(2)}`);
          return false;
        }
      } else if (surface.type === 'floor') {
        if (objectSize.x > surfaceWidth * 0.8 || objectSize.z > surfaceHeight * 0.8) {
          console.warn(`Object too large for floor surface: ${objectSize.x.toFixed(2)}x${objectSize.z.toFixed(2)} vs ${surfaceWidth.toFixed(2)}x${surfaceHeight.toFixed(2)}`);
          return false;
        }
      }
    }

    return true;
  }

  // Debug information
  getDebugInfo() {
    return {
      totalSurfaces: this.surfaces.size,
      occupiedCells: this.occupiedCells.size,
      surfaces: Array.from(this.surfaces.entries()).map(([id, surface]) => ({
        id,
        type: surface.type,
        dimensions: {
          width: surface.width,
          height: surface.height,
          depth: surface.depth
        },
        center: surface.center.toArray(),
        normal: surface.normal.toArray()
      }))
    };
  }
}

export default UniversalPlacementSystem;